import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { IconButton, Stack, Tooltip, Typography, type SxProps, type Theme } from '@mui/material';
import type { TypographyProps } from '@mui/material/Typography';

import type { ExamTaskRecord } from '../admin/examTaskReviewDataProvider';

function getTrimmedString(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}

function copyTextWithFallback(text: string): void {
    if (typeof document === 'undefined') {
        throw new Error('Clipboard is unavailable in this browser context.');
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);

    if (!copied) {
        throw new Error('Unable to copy Question No.');
    }
}

async function copyText(text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) {
        return;
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(trimmed);
        return;
    }

    copyTextWithFallback(trimmed);
}

export function getExamTaskQuestionNo(record: ExamTaskRecord | null | undefined): string | null {
    const source = record as Record<string, unknown> | null | undefined;

    return getTrimmedString(source?.questionNo) ?? getTrimmedString(source?.QuestionNo) ?? null;
}

type ExamTaskQuestionNoFieldProps = {
    value: string | null;
    label?: string;
    valueVariant?: TypographyProps['variant'];
    onCopied?: (value: string) => void;
    onCopyError?: (error: unknown) => void;
    sx?: SxProps<Theme>;
};

export function ExamTaskQuestionNoField({
    value,
    label = 'Question No',
    valueVariant = 'body2',
    onCopied,
    onCopyError,
    sx,
}: ExamTaskQuestionNoFieldProps) {
    if (!value) {
        return null;
    }

    const handleCopy = async () => {
        try {
            await copyText(value);
            onCopied?.(value);
        } catch (error) {
            onCopyError?.(error);
        }
    };

    return (
        <Stack spacing={0.25} sx={sx}>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography variant={valueVariant} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {value}
                </Typography>
                <Tooltip title="Copy Question No">
                    <IconButton size="small" aria-label={`Copy ${label}`} onClick={() => void handleCopy()} sx={{ flexShrink: 0 }}>
                        <ContentCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Stack>
    );
}